import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import { FiArrowLeft, FiRotateCcw, FiRefreshCw, FiSave, FiPlus, FiTrash2, FiX, FiEye, FiEyeOff, FiCode } from "react-icons/fi";
import PhonePreview from "../components/PhonePreview";
import VoiceoverEditor from "../components/VoiceoverEditor";
import DocViewerModal from "../components/DocViewerModal";
import useUnsavedChanges from "../hooks/useUnsavedChanges";

// ─── Design constants ───────────────────────────────────────────────
const TYPE_COLORS = {
    "Basics": "#2a9d8f",
    "Listening": "#e76f51",
    "Listening to English": "#e76f51",
    "Tracing": "#e9c46a",
    "Story": "#f4a261",
    "Story Review": "#f4a261",
    "Writing": "#ff7f9a",
    "Match": "#9b59b6",
    "Match to English": "#9b59b6",
    "Speaking": "#50C878",
    "Test": "#3a82c6",
    "Fill In Blank": "#6c5ce7",
    "Word Order": "#0984e3",
    "Image Choice": "#fd79a8",
    "True False": "#f39c12",
};
function typeColor(t) {
    return TYPE_COLORS[t] || "#cccccc";
}

const INPUT_STYLE = {
    fontFamily: "'Quicksand', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    padding: "10px 14px",
    borderRadius: 12,
    border: "1.5px solid #E0E0E0",
    background: "#f7f9fb",
    width: "100%",
    outline: "none",
    color: "#000",
    boxSizing: "border-box",
    transition: "border-color 0.18s",
};

const LABEL_STYLE = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "#606060",
    display: "block",
    marginBottom: 5,
};

const CARD_STYLE = {
    background: "#fff",
    borderRadius: 20,
    border: "1px solid #E0E0E0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "22px 24px",
    marginBottom: 20,
};

// ─── Field helper ────────────────────────────────────────────────────
function Field({ label, children }) {
    return (
        <div>
            <label style={LABEL_STYLE}>{label}</label>
            {children}
        </div>
    );
}

// ─── Concepts and lesson types ───────────────────────────────────────
const CONCEPTS = [
    "Introduction to the Alphabet and Pronunciation",
    "Basic Vocabulary and Phrases",
    "Basic Grammar",
    "Everyday Situations",
    "Cultural Context"
];
const LESSON_TYPES = [
    "Listening",
    "Listening to English",
    "Match to English",
    "Writing",
    "Story",
    "Story Review",
    "Basics",
    "Match",
    "Test",
    "Speaking",
    "Fill In Blank",
    "Word Order",
    "Image Choice",
    "True False"
];

// ─── Word count helpers (module-level) ───────────────────────────────
function getWordCount(text) {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
}
function getSpeakingTime(wordCount) {
    return (wordCount / 130).toFixed(2);
}

const getDefaultQuestion = (type = "Listening") => {
    switch (type) {
        case "Listening":
            return { type, translation: "", choices: [{ text: "", correct: false }, { text: "", correct: false }], videoUrl: "", line: "", writingMaterial: "", imageUrl: "", question: "" };
        case "Speaking":
            return { type, sentence: "" };
        case "Listening to English":
            return { type, amharic: "", choices: [{ text: "", correct: false }, { text: "", correct: false }], videoUrl: "", line: "", writingMaterial: "", imageUrl: "", question: "" };
        case "Match to English":
            return { type, choices: [{ amharic: "", english: "" }, { amharic: "", english: "" }], videoUrl: "", line: "", writingMaterial: "", imageUrl: "", question: "" };
        case "Match":
        case "Story Review":
        case "Test":
            return { type, choices: [{ text: "", correct: false }, { text: "", correct: false }], videoUrl: "", line: "", writingMaterial: "", imageUrl: "", question: "" };
        case "Writing":
            return { type, writingMaterial: "", translation: "", choices: [], videoUrl: "", line: "", imageUrl: "", question: "" };
        case "Story":
            return { type, line: "", translation: "", imageUrl: "", choices: [], videoUrl: "", writingMaterial: "", question: "" };
        case "Basics":
            return { type, videoUrl: "", firebaseUri: "", choices: [], line: "", writingMaterial: "", imageUrl: "", question: "" };
        case "Fill In Blank":
            return { type, sentence: "", choices: [{ text: "", correct: false }, { text: "", correct: false }], translation: "" };
        case "Word Order":
            return { type, sentence: "", words: [], prompt: "" };
        case "Image Choice":
            return { type, imageUrl: "", question: "", choices: [{ text: "", correct: false }, { text: "", correct: false }, { text: "", correct: false }, { text: "", correct: false }], translation: "" };
        case "True False":
            return { type, statement: "", choices: [{ text: "True", correct: true }, { text: "False", correct: false }], explanation: "" };
        default:
            return { type, choices: [], videoUrl: "", line: "", writingMaterial: "", imageUrl: "", question: "" };
    }
};

// ─── Spinner ──────────────────────────────────────────────────────────
function Spinner() {
    return (
        <>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #2a9d8f", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </>
    );
}

// ─── 3D icon button ───────────────────────────────────────────────────
function IconBtn({ onClick, disabled, title, children, bg = "#f0f0f0", color = "#333", borderColor = "#d0d0d0" }) {
    const [pressed, setPressed] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            onMouseDown={() => setPressed(true)}
            onMouseUp={() => setPressed(false)}
            onMouseLeave={() => setPressed(false)}
            style={{
                display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                fontFamily: "'Quicksand', sans-serif", padding: "8px 14px", borderRadius: 12,
                background: bg, color, cursor: disabled ? "not-allowed" : "pointer",
                border: `1.5px solid ${borderColor}`, borderBottomWidth: pressed ? 2 : 4,
                opacity: disabled ? 0.45 : 1,
                transform: pressed ? "translateY(2px)" : "none",
                transition: "transform 80ms, border-bottom-width 80ms",
            }}
        >
            {children}
        </button>
    );
}

function EditLesson() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const db = getFirestore();
    const questionsCardRef = useRef(null);

    const [title, setTitle] = useState("");
    const [concept, setConcept] = useState(CONCEPTS[0]);
    const [type, setType] = useState(LESSON_TYPES[0]);
    const [status, setStatus] = useState("published");
    const [cups, setCups] = useState("");
    const [description, setDescription] = useState("");
    const [number, setNumber] = useState("");
    const [basicsScript, setBasicsScript] = useState("");
    const [voiceoverAudioUrl, setVoiceoverAudioUrl] = useState("");
    const [voiceoverSegments, setVoiceoverSegments] = useState([]);
    const [voiceoverPeaks, setVoiceoverPeaks] = useState(null);
    const [voiceoverDuration, setVoiceoverDuration] = useState(0);
    const lessonStorageKeyRef = useRef(id);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [leaveTo, setLeaveTo] = useState(null);
    const [error, setError] = useState("");
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [previewQIndex, setPreviewQIndex] = useState(0);
    const [previewAnswerState, setPreviewAnswerState] = useState("idle");
    const [previewOpen, setPreviewOpen] = useState(true);
    const [rawDoc, setRawDoc] = useState(null);
    const [viewDocOpen, setViewDocOpen] = useState(false);

    const pushUndo = (qs) => {
        setUndoStack(stack => [...stack, JSON.stringify(qs)]);
        setRedoStack([]);
    };
    const handleUndo = () => {
        if (undoStack.length === 0) return;
        setRedoStack(stack => [...stack, JSON.stringify(questions)]);
        const prev = undoStack[undoStack.length - 1];
        setQuestions(JSON.parse(prev));
        setUndoStack(stack => stack.slice(0, -1));
    };
    const handleRedo = () => {
        if (redoStack.length === 0) return;
        setUndoStack(stack => [...stack, JSON.stringify(questions)]);
        const next = redoStack[redoStack.length - 1];
        setQuestions(JSON.parse(next));
        setRedoStack(stack => stack.slice(0, -1));
    };

    useEffect(() => {
        const fetchLesson = async () => {
            const lessonRef = doc(db, "lessons", id);
            const lessonSnap = await getDoc(lessonRef);
            if (lessonSnap.exists()) {
                const data = lessonSnap.data();
                setRawDoc({ id: lessonSnap.id, ...data });
                setTitle(data.title || "");
                setConcept(data.concept || CONCEPTS[0]);
                setType(data.type || LESSON_TYPES[0]);
                setStatus(data.status || "published");
                setCups(data.cups !== undefined ? String(data.cups) : "");
                setDescription(data.description || "");
                setNumber(data.number !== undefined ? String(data.number) : "");
                const loadedQuestions = Array.isArray(data.questions) && data.questions.length > 0
                    ? data.questions
                    : [getDefaultQuestion("Listening")];
                setQuestions(loadedQuestions);

                // Deep-link from a content report: ?q=<screenIndex> selects that question.
                const qParam = searchParams.get("q");
                if (qParam !== null) {
                    const qIndex = Math.min(Math.max(parseInt(qParam, 10) || 0, 0), loadedQuestions.length - 1);
                    setPreviewQIndex(qIndex);
                }
                setBasicsScript(data.script || "");
                setVoiceoverAudioUrl(data.voiceoverAudioUrl || "");
                setVoiceoverSegments(Array.isArray(data.voiceoverSegments) ? data.voiceoverSegments : []);
                setVoiceoverPeaks(Array.isArray(data.voiceoverPeaks) ? data.voiceoverPeaks : null);
                setVoiceoverDuration(data.voiceoverDuration || 0);
            }
            setLoading(false);
        };
        fetchLesson();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, id]);

    useEffect(() => {
        if (!loading && searchParams.get("q") !== null) {
            questionsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // Confirm-on-exit guard (in-app navigation + tab close). Turned off while a
    // save is running, before the lesson loads, or once a redirect is requested.
    useUnsavedChanges(!saving && !loading && leaveTo === null);

    useEffect(() => {
        if (leaveTo !== null) navigate(leaveTo);
    }, [leaveTo, navigate]);

    useEffect(() => {
        document.title = title.trim()
            ? `Editing: ${title.trim()} · Kubaya CMS`
            : "Edit Lesson · Kubaya CMS";
        return () => { document.title = "Kubaya CMS"; };
    }, [title]);

    const addQuestionPanel = (qType = "Listening") => {
        pushUndo(questions);
        setQuestions(qs => {
            const next = [...qs, getDefaultQuestion(qType)];
            setPreviewQIndex(next.length - 1);
            return next;
        });
        setPreviewAnswerState("idle");
    };

    const removeQuestionPanel = idx => {
        pushUndo(questions);
        setQuestions(qs => {
            const next = qs.filter((_, i) => i !== idx);
            setPreviewQIndex(p => Math.min(p, Math.max(0, next.length - 1)));
            return next;
        });
    };

    const handleQuestionChange = (idx, key, value) => {
        pushUndo(questions);
        setQuestions(qs => qs.map((q, i) => (i === idx ? { ...q, [key]: value } : q)));
    };

    const handleQuestionTypeChange = (idx, newType) => {
        pushUndo(questions);
        setQuestions(qs => qs.map((q, i) => (i === idx ? getDefaultQuestion(newType) : q)));
        setPreviewQIndex(idx);
        setPreviewAnswerState("idle");
    };

    const handleChoiceChange = (qIdx, cIdx, key, value) => {
        pushUndo(questions);
        setQuestions(qs =>
            qs.map((q, i) => {
                if (i !== qIdx) return q;
                if (q.type === "Match" || q.type === "Match to English") {
                    return { ...q, choices: q.choices.map((c, j) => j === cIdx ? { ...c, [key]: value } : c) };
                }
                if (key === "correct" && value) {
                    return { ...q, choices: q.choices.map((c, j) => ({ ...c, correct: j === cIdx })) };
                }
                return { ...q, choices: q.choices.map((c, j) => j === cIdx ? { ...c, [key]: value } : c) };
            })
        );
    };

    const addChoice = (qIdx) => {
        pushUndo(questions);
        setQuestions(qs =>
            qs.map((q, i) =>
                i === qIdx
                    ? { ...q, choices: [...(q.choices || []), q.type === "Match to English" ? { amharic: "", english: "" } : { text: "", correct: false }] }
                    : q
            )
        );
    };

    const removeChoice = (qIdx, cIdx) => {
        pushUndo(questions);
        setQuestions(qs =>
            qs.map((q, i) => i === qIdx ? { ...q, choices: q.choices.filter((_, j) => j !== cIdx) } : q)
        );
    };

    const validate = () => {
        if (!title.trim() || !concept.trim() || !type.trim() || !cups || !description.trim() || !number) {
            setError("Please fill in all lesson fields.");
            return false;
        }
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            switch (q.type) {
                case "Listening":
                case "Listening to English":
                    if (!q.choices || q.choices.length < 2 || q.choices.some(c => !c.text.trim())) {
                        setError(`All Listening questions must have at least 2 choices with text.`);
                        return false;
                    }
                    if (!q.choices.some(c => c.correct)) {
                        setError(`Each Listening question must have one correct choice.`);
                        return false;
                    }
                    if (q.type === "Listening to English" && (!q.amharic || !q.amharic.trim())) {
                        setError("Listening to English questions require the Amharic field.");
                        return false;
                    }
                    break;
                case "Match":
                    if (!q.choices || q.choices.length < 2 || q.choices.some(c => !c.text.trim())) {
                        setError(`All Match questions must have at least 2 choices with text.`);
                        return false;
                    }
                    break;
                case "Match to English":
                    if (!q.choices || q.choices.length < 2 || q.choices.some(c => !c.amharic?.trim() || !c.english?.trim())) {
                        setError("Match to English questions must have at least 2 rows with both Amharic and English.");
                        return false;
                    }
                    break;
                case "Story Review":
                    if (!q.question || !q.question.trim()) {
                        setError("Story Review questions require a question.");
                        return false;
                    }
                    if (!q.choices || q.choices.length < 2 || q.choices.some(c => !c.text.trim())) {
                        setError("Story Review questions must have at least 2 choices with text.");
                        return false;
                    }
                    if (!q.choices.some(c => c.correct)) {
                        setError("Each Story Review question must have one correct choice.");
                        return false;
                    }
                    break;
                case "Test":
                    if (!q.question || !q.question.trim()) {
                        setError("Test questions require a question.");
                        return false;
                    }
                    if (!q.choices || q.choices.length < 2 || q.choices.some(c => !c.text.trim())) {
                        setError("Test questions must have at least 2 choices with text.");
                        return false;
                    }
                    if (!q.choices.some(c => c.correct)) {
                        setError("Each Test question must have one correct choice.");
                        return false;
                    }
                    break;
                case "Writing":
                    if (!q.writingMaterial || !q.writingMaterial.trim()) {
                        setError("Writing questions require writing material.");
                        return false;
                    }
                    break;
                case "Story":
                    if (!q.line || !q.line.trim()) {
                        setError("Story questions require a line.");
                        return false;
                    }
                    break;
                case "Basics":
                    // Video URL is legacy/optional now that Basics lessons can use
                    // the voiceover editor (recording + parts) instead.
                    break;
                case "Speaking":
                    if (!q.sentence || !q.sentence.trim()) {
                        setError("Speaking questions require a sentence.");
                        return false;
                    }
                    break;
                case "Fill In Blank":
                    if (!q.sentence || !q.sentence.includes("___")) {
                        setError("Fill In Blank questions require a sentence containing ___ as the blank.");
                        return false;
                    }
                    if (!q.choices || q.choices.length < 2 || q.choices.some(c => !c.text.trim())) {
                        setError("Fill In Blank questions require at least 2 choices with text.");
                        return false;
                    }
                    if (!q.choices.some(c => c.correct)) {
                        setError("Each Fill In Blank question must have one correct choice.");
                        return false;
                    }
                    break;
                case "Word Order":
                    if (!q.sentence || !q.sentence.trim()) {
                        setError("Word Order questions require a correct sentence.");
                        return false;
                    }
                    if (!q.words || q.words.length < 2) {
                        setError("Word Order questions require at least 2 word tiles.");
                        return false;
                    }
                    break;
                case "Image Choice":
                    if (!q.imageUrl || !q.imageUrl.trim()) {
                        setError("Image Choice questions require an image URL.");
                        return false;
                    }
                    if (!q.choices || q.choices.length < 2 || q.choices.some(c => !c.text.trim())) {
                        setError("Image Choice questions require at least 2 choices with text.");
                        return false;
                    }
                    if (!q.choices.some(c => c.correct)) {
                        setError("Each Image Choice question must have one correct choice.");
                        return false;
                    }
                    break;
                case "True False":
                    if (!q.statement || !q.statement.trim()) {
                        setError("True/False questions require a statement.");
                        return false;
                    }
                    if (!q.choices || !q.choices.some(c => c.correct)) {
                        setError("True/False questions must have one correct answer selected.");
                        return false;
                    }
                    break;
                default:
                    break;
            }
        }
        setError("");
        return true;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        const lessonRef = doc(db, "lessons", id);
        const lessonData = { title, concept, type, status, cups: Number(cups), description, number: Number(number), questions };
        if (type === "Basics") {
            lessonData.script = basicsScript;
            lessonData.voiceoverAudioUrl = voiceoverAudioUrl;
            lessonData.voiceoverSegments = voiceoverSegments;
            lessonData.voiceoverPeaks = voiceoverPeaks;
            lessonData.voiceoverDuration = voiceoverDuration;
        }
        await updateDoc(lessonRef, lessonData);
        setSaving(false);
        alert("Lesson updated!");
        setLeaveTo("/");
    };

    const showCorrect = (qType) => qType === "Listening" || qType === "Listening to English" || qType === "Story Review" || qType === "Test";

    const renderQuestionFields = (q, idx) => {
        switch (q.type) {
            case "Listening":
            case "Listening to English":
            case "Match":
            case "Story Review":
            case "Test": {
                const hasCorrect = showCorrect(q.type);
                return (
                    <>
                        {q.type === "Listening" && (
                            <Field label="Translation">
                                <input
                                    type="text"
                                    value={q.translation || ""}
                                    onChange={e => handleQuestionChange(idx, "translation", e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    placeholder="Enter translation shown in popup"
                                />
                            </Field>
                        )}
                        {q.type === "Listening to English" && (
                            <Field label="Amharic">
                                <input
                                    type="text"
                                    value={q.amharic || ""}
                                    onChange={e => handleQuestionChange(idx, "amharic", e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    placeholder="Enter the Amharic form of the word"
                                    required
                                />
                            </Field>
                        )}
                        <div style={{ marginTop: 10 }}>
                            <label style={LABEL_STYLE}>Choices</label>
                            {(q.choices || []).map((choice, cIdx) => {
                                const isCorrect = hasCorrect && !!choice.correct;
                                return (
                                    <div key={cIdx} style={{
                                        display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                                        border: `1.5px solid ${isCorrect ? "#2a9d8f" : "#E0E0E0"}`,
                                        borderRadius: 12, marginBottom: 6,
                                        background: isCorrect ? "#f0faf9" : "#f7f9fb",
                                    }}>
                                        {hasCorrect && (
                                            <button
                                                type="button"
                                                onClick={() => handleChoiceChange(idx, cIdx, "correct", true)}
                                                title="Mark as correct"
                                                style={{
                                                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                                                    border: `2.5px solid ${isCorrect ? "#2a9d8f" : "#aaa"}`,
                                                    background: isCorrect ? "#2a9d8f" : "transparent",
                                                    cursor: "pointer", padding: 0,
                                                }}
                                            />
                                        )}
                                        <input
                                            type="text"
                                            value={choice.text}
                                            onChange={e => handleChoiceChange(idx, cIdx, "text", e.target.value)}
                                            placeholder={`Choice ${cIdx + 1}`}
                                            style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600, color: "#000" }}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeChoice(idx, cIdx)}
                                            disabled={(q.choices || []).length <= 2}
                                            style={{ background: "none", border: "none", color: "#e76f51", cursor: (q.choices || []).length <= 2 ? "not-allowed" : "pointer", opacity: (q.choices || []).length <= 2 ? 0.35 : 1, padding: 4, display: "flex", alignItems: "center" }}
                                        >
                                            <FiTrash2 size={15} />
                                        </button>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                onClick={() => addChoice(idx)}
                                style={{ width: "100%", padding: "8px 0", marginTop: 4, border: "1.5px dashed #2a9d8f", borderRadius: 12, background: "none", color: "#2a9d8f", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}
                            >
                                <FiPlus size={14} /> Add Choice
                            </button>
                        </div>
                    </>
                );
            }
            case "Speaking":
                return (
                    <Field label="Sentence">
                        <input
                            type="text"
                            value={q.sentence || ""}
                            onChange={e => handleQuestionChange(idx, "sentence", e.target.value)}
                            style={{ ...INPUT_STYLE }}
                            onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                            onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                            placeholder="Enter the sentence for speaking practice"
                            required
                        />
                    </Field>
                );
            case "Match to English":
                return (
                    <div>
                        <label style={LABEL_STYLE}>Pairs (Amharic / English)</label>
                        {(q.choices || []).map((choice, cIdx) => (
                            <div key={cIdx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1.5px solid #E0E0E0", borderRadius: 12, marginBottom: 6, background: "#f7f9fb" }}>
                                <input
                                    type="text"
                                    value={choice.amharic || ""}
                                    onChange={e => handleChoiceChange(idx, cIdx, "amharic", e.target.value)}
                                    placeholder={`Amharic ${cIdx + 1}`}
                                    style={{ flex: 1, minWidth: 0, border: "none", background: "transparent", outline: "none", fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600, color: "#000" }}
                                    required
                                />
                                <span style={{ color: "#aaa", fontSize: 14 }}>|</span>
                                <input
                                    type="text"
                                    value={choice.english || ""}
                                    onChange={e => handleChoiceChange(idx, cIdx, "english", e.target.value)}
                                    placeholder={`English ${cIdx + 1}`}
                                    style={{ flex: 1, minWidth: 0, border: "none", background: "transparent", outline: "none", fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600, color: "#000" }}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => removeChoice(idx, cIdx)}
                                    disabled={(q.choices || []).length <= 2}
                                    style={{ background: "none", border: "none", color: "#e76f51", cursor: (q.choices || []).length <= 2 ? "not-allowed" : "pointer", opacity: (q.choices || []).length <= 2 ? 0.35 : 1, padding: 4, display: "flex", alignItems: "center" }}
                                >
                                    <FiTrash2 size={15} />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => addChoice(idx)}
                            style={{ width: "100%", padding: "8px 0", marginTop: 4, border: "1.5px dashed #2a9d8f", borderRadius: 12, background: "none", color: "#2a9d8f", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}
                        >
                            <FiPlus size={14} /> Add Row
                        </button>
                    </div>
                );
            case "Writing":
                return (
                    <>
                        <Field label="Writing Material">
                            <input
                                type="text"
                                value={q.writingMaterial || ""}
                                onChange={e => handleQuestionChange(idx, "writingMaterial", e.target.value)}
                                style={{ ...INPUT_STYLE }}
                                onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                required
                            />
                        </Field>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Translation">
                                <input
                                    type="text"
                                    value={q.translation || ""}
                                    onChange={e => handleQuestionChange(idx, "translation", e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    placeholder="Enter translation shown in popup"
                                />
                            </Field>
                        </div>
                    </>
                );
            case "Story":
                return (
                    <>
                        <Field label="Line">
                            <input
                                type="text"
                                value={q.line || ""}
                                onChange={e => handleQuestionChange(idx, "line", e.target.value)}
                                style={{ ...INPUT_STYLE }}
                                onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                required
                            />
                        </Field>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Translation">
                                <input
                                    type="text"
                                    value={q.translation || ""}
                                    onChange={e => handleQuestionChange(idx, "translation", e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    placeholder="Enter translation shown in popup"
                                />
                            </Field>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Image URL">
                                <input
                                    type="text"
                                    value={q.imageUrl || ""}
                                    onChange={e => handleQuestionChange(idx, "imageUrl", e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                />
                            </Field>
                            {q.imageUrl && (
                                <div style={{ marginTop: 8 }}>
                                    <img src={q.imageUrl} alt="Preview" style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 10, border: "1px solid #E0E0E0" }} onError={e => { e.target.style.display = "none"; }} />
                                </div>
                            )}
                        </div>
                    </>
                );
            case "Basics": {
                const isDirectVideo = q.videoUrl && /\.(mp4|webm|ogg)$/i.test(q.videoUrl);
                const ytMatch = q.videoUrl && q.videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                return (
                    <>
                        <Field label="Video URL">
                            <input
                                type="text"
                                value={q.videoUrl || ""}
                                onChange={e => handleQuestionChange(idx, "videoUrl", e.target.value)}
                                style={{ ...INPUT_STYLE }}
                                onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                required
                            />
                        </Field>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Firebase URI">
                                <input
                                    type="text"
                                    value={q.firebaseUri || ""}
                                    onChange={e => handleQuestionChange(idx, "firebaseUri", e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    placeholder="Enter Firebase URI"
                                />
                            </Field>
                        </div>
                        {q.videoUrl && (
                            <div style={{ marginTop: 10 }}>
                                {isDirectVideo ? (
                                    <video src={q.videoUrl} controls style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 10, border: "1px solid #E0E0E0" }} preload="metadata">
                                        Sorry, your browser doesn't support embedded videos.
                                    </video>
                                ) : ytMatch ? (
                                    <iframe
                                        width="320" height="180"
                                        src={`https://www.youtube.com/embed/${ytMatch[1]}`}
                                        title="YouTube video preview"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        style={{ borderRadius: 10, border: "1px solid #E0E0E0" }}
                                    />
                                ) : (
                                    <div style={{ color: "#888", fontSize: 13, marginTop: 6 }}>
                                        Cannot preview this video URL. Only direct video files (.mp4, .webm, .ogg) or YouTube links are supported.
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                );
            }
            case "Fill In Blank": {
                return (
                    <>
                        <Field label="Sentence (use ___ for the blank)">
                            <input
                                type="text"
                                value={q.sentence || ""}
                                onChange={e => handleQuestionChange(idx, "sentence", e.target.value)}
                                style={{ ...INPUT_STYLE }}
                                onFocus={e => e.target.style.borderColor = "#6c5ce7"}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                placeholder='e.g. "The ___ is big"'
                                required
                            />
                        </Field>
                        <div style={{ marginTop: 10 }}>
                            <label style={LABEL_STYLE}>Choices — select the correct one (2–4)</label>
                            {(q.choices || []).map((choice, cIdx) => {
                                const isCorrect = !!choice.correct;
                                return (
                                    <div key={cIdx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1.5px solid ${isCorrect ? "#6c5ce7" : "#E0E0E0"}`, borderRadius: 12, marginBottom: 6, background: isCorrect ? "#f4f0ff" : "#f7f9fb" }}>
                                        <button type="button" onClick={() => handleChoiceChange(idx, cIdx, "correct", true)} title="Mark as correct" style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, border: `2.5px solid ${isCorrect ? "#6c5ce7" : "#aaa"}`, background: isCorrect ? "#6c5ce7" : "transparent", cursor: "pointer", padding: 0 }} />
                                        <input type="text" value={choice.text} onChange={e => handleChoiceChange(idx, cIdx, "text", e.target.value)} placeholder={`Choice ${cIdx + 1}`} style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600, color: "#000" }} />
                                        <button type="button" onClick={() => removeChoice(idx, cIdx)} disabled={(q.choices || []).length <= 2} style={{ background: "none", border: "none", color: "#e76f51", cursor: (q.choices || []).length <= 2 ? "not-allowed" : "pointer", opacity: (q.choices || []).length <= 2 ? 0.35 : 1, padding: 4, display: "flex", alignItems: "center" }}><FiTrash2 size={15} /></button>
                                    </div>
                                );
                            })}
                            <button type="button" onClick={() => addChoice(idx)} disabled={(q.choices || []).length >= 4} style={{ width: "100%", padding: "8px 0", marginTop: 4, border: "1.5px dashed #6c5ce7", borderRadius: 12, background: "none", color: "#6c5ce7", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13, cursor: (q.choices || []).length >= 4 ? "not-allowed" : "pointer", opacity: (q.choices || []).length >= 4 ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                                <FiPlus size={14} /> Add Choice
                            </button>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Translation (optional — shown after answering)">
                                <input type="text" value={q.translation || ""} onChange={e => handleQuestionChange(idx, "translation", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#2a9d8f"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder="e.g. Amharic translation of the correct word" />
                            </Field>
                        </div>
                    </>
                );
            }
            case "Word Order": {
                const inputId = `worder-input-${idx}`;
                const addWord = () => {
                    const el = document.getElementById(inputId);
                    const val = el?.value?.trim();
                    if (!val) return;
                    handleQuestionChange(idx, "words", [...(q.words || []), val]);
                    if (el) el.value = "";
                };
                const removeWord = (wIdx) => {
                    pushUndo(questions);
                    setQuestions(qs => qs.map((question, i) => i === idx ? { ...question, words: (question.words || []).filter((_, j) => j !== wIdx) } : question));
                };
                return (
                    <>
                        <Field label="Correct Sentence (exact answer the app compares against)">
                            <input type="text" value={q.sentence || ""} onChange={e => handleQuestionChange(idx, "sentence", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#0984e3"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder='e.g. "እኔ ቤት ነኝ"' required />
                        </Field>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Prompt (optional)">
                                <input type="text" value={q.prompt || ""} onChange={e => handleQuestionChange(idx, "prompt", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#2a9d8f"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder='Default: "Put the words in the correct order"' />
                            </Field>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <label style={LABEL_STYLE}>Word Tiles (correct words + 1–3 distractors)</label>
                            {(q.words || []).length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                                    {(q.words || []).map((word, wIdx) => (
                                        <div key={wIdx} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", border: "1.5px solid #0984e3", borderRadius: 8, background: "#f0f7ff" }}>
                                            <span style={{ fontWeight: 600, fontSize: 13, color: "#0984e3" }}>{word}</span>
                                            <button type="button" onClick={() => removeWord(wIdx)} style={{ background: "none", border: "none", color: "#e76f51", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}><FiX size={12} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div style={{ display: "flex", gap: 8 }}>
                                <input id={inputId} type="text" placeholder="Type a word, press Enter or + to add" style={{ ...INPUT_STYLE, flex: 1 }} onFocus={e => e.target.style.borderColor = "#0984e3"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addWord(); } }} />
                                <button type="button" onClick={addWord} style={{ padding: "10px 14px", borderRadius: 12, border: "1.5px solid #0984e3", borderBottomWidth: 3, background: "#0984e3", color: "#fff", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center" }}><FiPlus size={14} /></button>
                            </div>
                        </div>
                    </>
                );
            }
            case "Image Choice": {
                return (
                    <>
                        <Field label="Image URL">
                            <input type="text" value={q.imageUrl || ""} onChange={e => handleQuestionChange(idx, "imageUrl", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#fd79a8"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder="https://example.com/image.jpg" required />
                        </Field>
                        {q.imageUrl && (
                            <div style={{ marginTop: 8, marginBottom: 4 }}>
                                <img src={q.imageUrl} alt="Preview" style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 10, border: "1px solid #E0E0E0" }} onError={e => { e.target.style.display = "none"; }} />
                            </div>
                        )}
                        <div style={{ marginTop: 10 }}>
                            <Field label="Question (optional)">
                                <input type="text" value={q.question || ""} onChange={e => handleQuestionChange(idx, "question", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#2a9d8f"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder='e.g. "What is this called in Amharic?"' />
                            </Field>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <label style={LABEL_STYLE}>Choices — select the correct one (4 recommended for 2×2 grid)</label>
                            {(q.choices || []).map((choice, cIdx) => {
                                const isCorrect = !!choice.correct;
                                return (
                                    <div key={cIdx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1.5px solid ${isCorrect ? "#2a9d8f" : "#E0E0E0"}`, borderRadius: 12, marginBottom: 6, background: isCorrect ? "#f0faf9" : "#f7f9fb" }}>
                                        <span style={{ fontWeight: 700, color: "#888", minWidth: 18, textAlign: "center", fontSize: 13 }}>{String.fromCharCode(65 + cIdx)}</span>
                                        <button type="button" onClick={() => handleChoiceChange(idx, cIdx, "correct", true)} title="Mark as correct" style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, border: `2.5px solid ${isCorrect ? "#2a9d8f" : "#aaa"}`, background: isCorrect ? "#2a9d8f" : "transparent", cursor: "pointer", padding: 0 }} />
                                        <input type="text" value={choice.text} onChange={e => handleChoiceChange(idx, cIdx, "text", e.target.value)} placeholder={`Choice ${String.fromCharCode(65 + cIdx)}`} style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600, color: "#000" }} />
                                        <button type="button" onClick={() => removeChoice(idx, cIdx)} disabled={(q.choices || []).length <= 2} style={{ background: "none", border: "none", color: "#e76f51", cursor: (q.choices || []).length <= 2 ? "not-allowed" : "pointer", opacity: (q.choices || []).length <= 2 ? 0.35 : 1, padding: 4, display: "flex", alignItems: "center" }}><FiTrash2 size={15} /></button>
                                    </div>
                                );
                            })}
                            <button type="button" onClick={() => addChoice(idx)} disabled={(q.choices || []).length >= 4} style={{ width: "100%", padding: "8px 0", marginTop: 4, border: "1.5px dashed #2a9d8f", borderRadius: 12, background: "none", color: "#2a9d8f", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13, cursor: (q.choices || []).length >= 4 ? "not-allowed" : "pointer", opacity: (q.choices || []).length >= 4 ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                                <FiPlus size={14} /> Add Choice
                            </button>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Translation (optional — shown after answering)">
                                <input type="text" value={q.translation || ""} onChange={e => handleQuestionChange(idx, "translation", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#2a9d8f"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder="e.g. English translation of the correct word" />
                            </Field>
                        </div>
                    </>
                );
            }
            case "True False":
                return (
                    <>
                        <Field label="Statement">
                            <input type="text" value={q.statement || ""} onChange={e => handleQuestionChange(idx, "statement", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#f39c12"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder='"ሰላም" means "Hello"' required />
                        </Field>
                        <div style={{ marginTop: 12 }}>
                            <label style={LABEL_STYLE}>Correct Answer</label>
                            <div style={{ display: "flex", gap: 10 }}>
                                {[{ text: "True", color: "#2a9d8f", icon: "✓" }, { text: "False", color: "#e76f51", icon: "✗" }].map(({ text, color, icon }) => {
                                    const isSelected = (q.choices || []).find(c => c.text === text)?.correct;
                                    return (
                                        <button key={text} type="button" onClick={() => handleQuestionChange(idx, "choices", [{ text: "True", correct: text === "True" }, { text: "False", correct: text === "False" }])} style={{ flex: 1, padding: "12px", borderRadius: 12, border: `2px solid ${isSelected ? color : "#E0E0E0"}`, background: isSelected ? `${color}18` : "#f7f9fb", color: isSelected ? color : "#888", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                                            {icon} {text}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Explanation (optional — shown after answering)">
                                <input type="text" value={q.explanation || ""} onChange={e => handleQuestionChange(idx, "explanation", e.target.value)} style={{ ...INPUT_STYLE }} onFocus={e => e.target.style.borderColor = "#2a9d8f"} onBlur={e => e.target.style.borderColor = "#E0E0E0"} placeholder="Write neutrally so it reads correctly whether user was right or wrong" />
                            </Field>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, fontFamily: "'Quicksand', sans-serif" }}>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 36, height: 36, border: "3px solid #E0E0E0", borderTop: "3px solid #2a9d8f", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#888" }}>Loading lesson…</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1160, margin: "0 auto" }}>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

            {/* Page header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                        type="button"
                        onClick={() => navigate("/lessons")}
                        className="primary-button"
                        style={{ backgroundColor: "#f0f0f0", color: "#333", borderColor: "#d0d0d0", display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, padding: "9px 16px" }}
                    >
                        <FiArrowLeft size={15} /> Back
                    </button>
                    <h1 style={{ margin: 0, fontSize: "1.6rem", color: "#2a9d8f", fontWeight: 700 }}>Edit Lesson</h1>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <IconBtn onClick={() => setViewDocOpen(true)} title="View Firestore document" bg="#fff" color="#3a82c6" borderColor="#3a82c6">
                        <FiCode size={14} /> View JSON
                    </IconBtn>
                    <IconBtn onClick={handleUndo} disabled={undoStack.length === 0} title="Undo">
                        <FiRotateCcw size={14} /> Undo
                    </IconBtn>
                    <IconBtn onClick={handleRedo} disabled={redoStack.length === 0} title="Redo">
                        <FiRefreshCw size={14} /> Redo
                    </IconBtn>
                    <button
                        type="submit"
                        form="lesson-form"
                        className="primary-button"
                        disabled={saving}
                        style={{ backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80", display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, padding: "9px 20px" }}
                    >
                        {saving ? <Spinner /> : <FiSave size={14} />}
                        {saving ? "Saving…" : "Save Changes"}
                    </button>
                </div>
            </div>

            {/* Two-panel split — phone-first layout */}
            <div className="lesson-editor-grid" style={{ display: "grid", gridTemplateColumns: previewOpen ? "260px 1fr" : "60px 1fr", gap: 20, alignItems: "start", transition: "grid-template-columns 0.15s ease" }}>

                {/* LEFT — phone preview, purely visual (collapsible) */}
                <div style={{ position: "sticky", top: 20 }}>
                    {previewOpen ? (
                        <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, padding: "0 2px" }}>
                                <span style={LABEL_STYLE}>Preview</span>
                                <button
                                    type="button"
                                    onClick={() => setPreviewOpen(false)}
                                    title="Hide preview"
                                    style={{ background: "none", border: "none", color: "#888", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: 6 }}
                                >
                                    <FiEyeOff size={13} /> Hide
                                </button>
                            </div>

                            {/* Answer state toggle — see how the screen looks in each state */}
                            {questions.length > 0 && ["Listening","Listening to English","Test","Story Review","Fill In Blank","Word Order","Image Choice","True False"].includes(questions[previewQIndex].type) && (
                                <div style={{ display: "flex", gap: 4, marginBottom: 10, background: "#f0f0f0", borderRadius: 10, padding: 4 }}>
                                    {[{ key: "idle", label: "Idle" }, { key: "correct", label: "✓ Correct" }, { key: "wrong", label: "✗ Wrong" }].map(({ key, label }) => {
                                        const active = previewAnswerState === key;
                                        const bg = key === "correct" ? "#50C878" : key === "wrong" ? "#e76f51" : "#fff";
                                        return (
                                            <button key={key} type="button" onClick={() => setPreviewAnswerState(key)}
                                                style={{ flex: 1, padding: "8px 0", borderRadius: 7, border: "none", background: active ? bg : "transparent", color: active ? (key === "idle" ? "#333" : "#fff") : "#888", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                                            >{label}</button>
                                        );
                                    })}
                                </div>
                            )}

                            <PhonePreview
                                question={questions[previewQIndex]}
                                answerState={previewAnswerState}
                                scale={0.6}
                                voiceoverAudioUrl={voiceoverAudioUrl}
                                voiceoverSegments={voiceoverSegments}
                            />
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            title="Show preview"
                            style={{ width: "100%", height: 132, borderRadius: 14, border: "1.5px solid #E0E0E0", borderBottomWidth: 4, background: "#fff", color: "#2a9d8f", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
                        >
                            <FiEye size={18} />
                            <span style={{ writingMode: "vertical-rl", fontFamily: "'Quicksand', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em" }}>PREVIEW</span>
                        </button>
                    )}
                </div>

                {/* RIGHT — form fields */}
                <form id="lesson-form" onSubmit={handleSave} style={{ minWidth: 0 }}>
                    {error && (
                        <div style={{ background: "#fde8e8", border: "1px solid #f5c6c6", borderRadius: 12, padding: "12px 16px", color: "#c0392b", fontWeight: 600, fontSize: 14, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    {/* Lesson details card */}
                    <div style={CARD_STYLE}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#2a9d8f", marginBottom: 18 }}>Lesson Details</div>
                        <div className="lesson-fields-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                            <div style={{ gridColumn: "1 / -1" }}>
                                <Field label="Title">
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        style={{ ...INPUT_STYLE }}
                                        onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                        onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                        required
                                        placeholder="Lesson title"
                                    />
                                </Field>
                            </div>
                            <Field label="Concept">
                                <select
                                    value={concept}
                                    onChange={e => setConcept(e.target.value)}
                                    style={{ ...INPUT_STYLE, cursor: "pointer" }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    required
                                >
                                    {CONCEPTS.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </Field>
                            <Field label="Type">
                                <select
                                    value={type}
                                    onChange={e => setType(e.target.value)}
                                    style={{ ...INPUT_STYLE, cursor: "pointer" }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    required
                                >
                                    {LESSON_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </Field>
                            <Field label="Cups">
                                <input
                                    type="number"
                                    value={cups}
                                    onChange={e => setCups(e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                />
                            </Field>
                            <Field label="Number">
                                <input
                                    type="number"
                                    value={number}
                                    onChange={e => setNumber(e.target.value)}
                                    style={{ ...INPUT_STYLE }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                />
                            </Field>
                            <Field label="Status">
                                <select
                                    value={status}
                                    onChange={e => setStatus(e.target.value)}
                                    style={{ ...INPUT_STYLE, cursor: "pointer" }}
                                    onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                    onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                >
                                    <option value="draft">Draft — hidden from users</option>
                                    <option value="beta">Beta — visible, tagged "Beta"</option>
                                    <option value="published">Published</option>
                                </select>
                            </Field>
                            <div style={{ gridColumn: "1 / -1" }}>
                                <Field label="Description">
                                    <textarea
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={3}
                                        style={{ ...INPUT_STYLE, resize: "vertical" }}
                                        onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                        onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                    />
                                </Field>
                            </div>
                        </div>
                    </div>

                    {/* Basics script card */}
                    {type === "Basics" && (
                        <div style={{ ...CARD_STYLE, borderLeft: "4px solid #2a9d8f" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#2a9d8f", marginBottom: 12 }}>Script for Video</div>
                            <textarea
                                value={basicsScript}
                                onChange={e => setBasicsScript(e.target.value)}
                                rows={5}
                                style={{ ...INPUT_STYLE, resize: "vertical" }}
                                onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                placeholder="Write the script for the video here…"
                            />
                            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                                Word count: <strong>{getWordCount(basicsScript)}</strong> &nbsp;·&nbsp; Approx. speaking time: <strong>{getSpeakingTime(getWordCount(basicsScript))} min</strong>
                            </div>
                        </div>
                    )}

                    {/* Voiceover editor — upload, split into parts, assign script + image per part */}
                    {type === "Basics" && (
                        <div style={{ ...CARD_STYLE, borderLeft: "4px solid #2a9d8f" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#2a9d8f", marginBottom: 12 }}>Voiceover</div>
                            <VoiceoverEditor
                                audioUrl={voiceoverAudioUrl}
                                segments={voiceoverSegments}
                                peaks={voiceoverPeaks}
                                duration={voiceoverDuration}
                                script={basicsScript}
                                lessonKey={lessonStorageKeyRef.current}
                                onAudioChange={setVoiceoverAudioUrl}
                                onSegmentsChange={setVoiceoverSegments}
                                onPeaksChange={(p, d) => { setVoiceoverPeaks(p); setVoiceoverDuration(d); }}
                            />
                        </div>
                    )}

                    {/* Questions — direct-access tabs + type picker, right next to the fields */}
                    {questions.length > 0 && (
                        <div ref={questionsCardRef} style={{ ...CARD_STYLE, borderLeft: `4px solid ${typeColor(questions[previewQIndex].type)}` }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#2a9d8f", marginBottom: 12 }}>Questions</div>

                            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6, marginBottom: 12 }}>
                                {questions.map((q, i) => {
                                    const active = i === previewQIndex;
                                    const c = typeColor(q.type);
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => { setPreviewQIndex(i); setPreviewAnswerState("idle"); }}
                                            title={q.type}
                                            style={{ flexShrink: 0, minWidth: 36, height: 36, borderRadius: 10, border: `1.5px solid ${c}`, borderBottomWidth: active ? 3 : 1.5, background: active ? c : `${c}18`, color: active ? "#fff" : c, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'Quicksand', sans-serif" }}
                                        >
                                            {i + 1}
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={() => addQuestionPanel("Listening")}
                                    title="Add question"
                                    style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 10, border: "1.5px dashed #2a9d8f", background: "#fff", color: "#2a9d8f", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    <FiPlus size={15} />
                                </button>
                            </div>

                            <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                                <select
                                    value={questions[previewQIndex].type}
                                    onChange={e => handleQuestionTypeChange(previewQIndex, e.target.value)}
                                    style={{ ...INPUT_STYLE, flex: 1, cursor: "pointer", borderColor: typeColor(questions[previewQIndex].type), borderBottomWidth: 3, color: typeColor(questions[previewQIndex].type), fontWeight: 700, background: `${typeColor(questions[previewQIndex].type)}18` }}
                                >
                                    {LESSON_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => removeQuestionPanel(previewQIndex)}
                                    disabled={questions.length === 1}
                                    title="Delete this question"
                                    style={{ width: 44, flexShrink: 0, borderRadius: 12, border: "1.5px solid #e76f51", borderBottomWidth: 3, background: "#fff", color: "#e76f51", cursor: questions.length === 1 ? "not-allowed" : "pointer", opacity: questions.length === 1 ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    <FiTrash2 size={15} />
                                </button>
                            </div>
                            {(questions[previewQIndex].type === "Story Review" || questions[previewQIndex].type === "Test") && (
                                <div style={{ marginBottom: 12 }}>
                                    <Field label="Question Text">
                                        <input
                                            type="text"
                                            value={questions[previewQIndex].question || ""}
                                            onChange={e => handleQuestionChange(previewQIndex, "question", e.target.value)}
                                            style={{ ...INPUT_STYLE }}
                                            onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                            onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                            required
                                            placeholder="Enter the question"
                                        />
                                    </Field>
                                </div>
                            )}
                            {renderQuestionFields(questions[previewQIndex], previewQIndex)}
                        </div>
                    )}
                </form>

            </div>{/* end two-panel grid */}

            <DocViewerModal
                open={viewDocOpen}
                onClose={() => setViewDocOpen(false)}
                path={`lessons/${id}`}
                data={rawDoc}
            />
        </div>
    );
}

export default EditLesson;
